import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import type { CategoryResponse } from "@/shared/api/generated/iSHONCHPlatformAPI.schemas";
import { categoryIcon } from "@/shared/lib/categoryIcons";

/**
 * Mega menu under the header (Figma "Каталог" menu): top-level categories in three columns,
 * each with its subcategories in one dotted line. Closes on Escape and on navigation.
 */
export function CatalogMenu({
  categories,
  onClose,
}: {
  categories: CategoryResponse[];
  onClose: () => void;
}) {
  const { t } = useTranslation("shop");

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  return (
    <>
      <div
        aria-hidden
        className="fixed inset-0 top-0 z-30 bg-night/50"
        onClick={onClose}
        data-testid="catalog-menu-backdrop"
      />
      <nav
        aria-label={t("menuTitle")}
        className="absolute inset-x-0 top-0 z-40 max-h-[calc(100dvh-8rem)] overflow-y-auto rounded-b-3xl border-t border-line bg-surface shadow-xl"
      >
        <ul className="container-page grid gap-x-8 gap-y-7 py-8 sm:grid-cols-2 lg:grid-cols-3 lg:py-12">
          {categories.map((category) => {
            const Icon = categoryIcon(category.pc_icon);
            return (
              <li key={category.pc_id} className="flex gap-4">
                <span className="flex size-10 shrink-0 items-center justify-center rounded-lg2 bg-canvas text-brand-500">
                  <Icon className="size-5" aria-hidden />
                </span>
                <div className="flex min-w-0 flex-col gap-1">
                  <Link
                    to={`/catalog/${category.pc_slug}`}
                    onClick={onClose}
                    className="text-sm font-semibold text-ink-strong hover:text-brand-600"
                  >
                    {category.pc_name}
                  </Link>
                  <ul className="flex flex-wrap items-center text-body-md text-muted">
                    {category.pc_children.map((child, index) => (
                      <li key={child.pc_id} className="flex items-center">
                        {index > 0 && (
                          <span aria-hidden className="mx-2 size-1 rounded-full bg-line-strong" />
                        )}
                        <Link
                          to={`/catalog/${child.pc_slug}`}
                          onClick={onClose}
                          className="hover:text-brand-600"
                        >
                          {child.pc_name}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              </li>
            );
          })}
        </ul>
      </nav>
    </>
  );
}
